import { gql } from "@apollo/client"
import { faEye, faNoteSticky, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import If from "components/common/If";
import MoneyView from "components/common/views/MoneyView";
import ModalPortal from "components/modal/ModalPortal";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { useRef, useState } from "react";
import { GetTransactionsDataListQuery, GetTransactionsDataListQueryVariables, SortEnumType, WalletTransactionFilterInput, WalletTransactionSortInput } from "types/graphql";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import WalletTransactionFrom from "./transactions/WalletTransactionFrom";
import PropertyView from "components/common/views/PropertyView";
import UserPage from "pages/settings/users/UserPage";
import { useParams } from "react-router-dom";
import Button from "components/common/button/Button";
import WalletTransactionView from "./transactions/WalletTransactionView";

type WalletTransactionsDatalistProps = {
    walletId: number,
    name: string,
    allowAdd?: boolean,
    onItemChange?: (action: 'add' | 'update' | 'delete') => void | Promise<void>
}

type DataItem = NonNullable<GetTransactionsDataListQuery['walletTransactions']>['items'][0];

var query = gql`
query GetTransactionsDataList($where: WalletTransactionFilterInput, $take: Int, $skip: Int, $sort:[WalletTransactionSortInput!]) {
  walletTransactions {
    items(where: $where, skip: $skip, take:$take, order: $sort) {
      walletId
      id,
      amount
      transactionDate,
      transactionId,
      note
    }
    count(where: $where),
    amountSum(where: $where)
  }
}
`;

const WalletTransactionsDatalist = (props: WalletTransactionsDatalistProps) => {
    const MODAL_ID_FORM = `wallet-transaction-form-${props.name}-${props.walletId}`;
    const MODAL_ID_VIEW = `wallet-transaction-view-${props.name}-${props.walletId}`;
    const context = useAppContext();
    const [totalAmount, setTotalAmount] = useState(0);
    const [viewModel, setViewModel] = useState<DataItem | null>(null);
    const dataListRef = useRef<Datalist<DataItem, WalletTransactionFilterInput, WalletTransactionSortInput>>(null);

    const config: DatalistPropsType<DataItem, WalletTransactionFilterInput, WalletTransactionSortInput> = {
        itemId(i) {
            return i.id;
        },
        dataSource: {
            request: async (params) => {
                try {
                    const variables: GetTransactionsDataListQueryVariables = {
                        take: params.take,
                        skip: params.skip,
                        where: params.filters,
                        sort: params.sort
                    };

                    if (params.searchText) {
                        const searchTextWhere: WalletTransactionFilterInput = {
                            note: {
                                contains: params.searchText
                            },
                            transactionId: {
                                contains: params.searchText
                            }
                        };

                        variables.where = appendFilters(variables.where, searchTextWhere);
                    }

                    variables.where = appendFilters(variables.where, {
                        walletId: {
                            eq: props.walletId
                        }
                    });

                    var response = await graph<GetTransactionsDataListQuery, GetTransactionsDataListQueryVariables>({
                        query: query,
                        variables: variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    setTotalAmount(response.data?.walletTransactions?.amountSum || 0);

                    return {
                        items: response.data?.walletTransactions?.items || [],
                        count: response.data?.walletTransactions?.count || 0
                    };
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    id: uuid(),
                    name: 'transactionDate',
                    direction: SortEnumType.Desc
                }
            ]
        },
        name: props.name + "wallet-transactions-datalist",
        table: {
            columns: [
                {
                    name: 'id',
                    hide: true
                },
                {
                    name: 'transactionDate',
                    title: 'fields.date.name',
                    type: 'date',
                    filter: {
                        type: 'date'
                    },
                    sort: true,
                    card: {
                        group: 'first-row'
                    }
                },
                {
                    name: 'amount',
                    title: 'fields.amount.name',
                    type: 'number',
                    filter: {
                        type: 'number'
                    },
                    sort: true,
                    card: {
                        group: 'first-row'
                    },
                    resolve: i => <MoneyView value={i.amount} />,
                },
                {
                    name: 'transactionId',
                    title: 'fields.transaction-id.name',
                    type: 'string',
                    filter: {
                        type: 'string'
                    },
                    sort: true,
                    card: {
                        hide: true
                    }
                },
                {
                    name: 'note',
                    title: 'fields.note.name',
                    type: 'string',
                    filter: {
                        type: 'string'
                    },
                    card: {
                        group: 'first-row'
                    },
                    resolve: i => <If condition={i.note}>
                        <FontAwesomeIcon icon={faNoteSticky} />
                    </If>
                }
            ],
            render: {
                card(item, index) {
                    return (
                        <div
                            key={`wallet-transaction-details-${props.name}-${item.id}`}
                            className="flex justify-content-between border theme-border p-2 mb-4"
                        >
                            <div className="w-full">
                                <PropertyView label='fields.date.name' value={item.transactionDate} propertyType="datetime" />
                                <PropertyView label='fields.amount.name' value={item.amount} propertyType="money" />
                                {item.note && <FontAwesomeIcon icon={faNoteSticky} />}
                            </div>
                            <div className="my-auto">
                                <Button title={context.t('button.view')} onClick={() => openView(item)} theme="link primary">
                                    <FontAwesomeIcon icon={faEye} />
                                </Button>
                            </div>
                        </div>
                    )
                },
                footer: {
                    table: {
                        columns: [
                            {
                                name: 'total',
                                value: context.t('fields.total.name')
                            },
                            {
                                name: 'amountSum',
                                value: <MoneyView value={totalAmount} />
                            }
                        ]
                    },
                    card: {
                        element: <div>
                            <span>{context.t('fields.total.name')}: </span>
                            <MoneyView value={totalAmount} />
                        </div>,
                        bottom: 'bottom-app-header-negative'
                    }
                }
            }
        },
        header: {
            rightButtons: [
                {
                    icon: faPlus,
                    title: context.t('button.add'),
                    onClick: () => {
                        context.modal.open(MODAL_ID_FORM);
                    },
                    name: `wallet-transaction-form-add-${props.name}-${props.walletId}`
                }
            ]
        }
    }

    const openView = (item: DataItem) => {
        setViewModel(item);
        context.modal.open(MODAL_ID_VIEW);
    }

    const onUpdateOrDeleteView = (action: 'update' | 'delete') => {
        context.navigate(-1);
        dataListRef.current?.reload();
        props.onItemChange?.(action);
    }

    const onTransactionSave = () => {
        context.navigate(-1);
        dataListRef.current?.reload();
        props.onItemChange?.('add');
    }

    return <>
        <Datalist ref={dataListRef} {...config} />
        <ModalPortal name={MODAL_ID_FORM} title="common.add-new"  >
            <WalletTransactionFrom walletId={props.walletId} onSave={onTransactionSave} />
        </ModalPortal>
        <ModalPortal onClose={() => setViewModel(null)} name={MODAL_ID_VIEW} title="common.view">
            <WalletTransactionView
                model={viewModel || undefined}
                onDelete={() => onUpdateOrDeleteView('delete')}
                onUpdated={() => onUpdateOrDeleteView('update')}
            />
        </ModalPortal>
    </>;
}

export default WalletTransactionsDatalist;
